import { db } from './firebase';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';

export interface FunnelAnalytics {
  visitors: number;
  formClicks: number;
  submissions: number;
  dailyStats: Array<{
    date: string;
    visitors: number;
    submissions: number;
  }>;
}

export const incrementFunnelMetric = async (funnelId: string, metric: 'visitors' | 'formClicks' | 'submissions') => {
  const funnelRef = doc(db, 'funnels', funnelId);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const updates: Record<string, any> = {
    [`analytics.${metric}`]: increment(1),
    [`analytics.dailyStats.${today.toISOString().split('T')[0]}.${metric}`]: increment(1)
  };
  
  await updateDoc(funnelRef, updates);
};

export const getFunnelAnalytics = async (funnelId: string): Promise<FunnelAnalytics> => {
  // Get funnel analytics data
  const funnelRef = doc(db, 'funnels', funnelId);
  const funnelDoc = await getDoc(funnelRef);
  
  if (!funnelDoc.exists()) {
    throw new Error('Funnel not found');
  }

  // Get submissions count from the new funnel_submissions collection
  const submissionsRef = doc(db, 'funnel_submissions', funnelId);
  const submissionsDoc = await getDoc(submissionsRef);
  
  const submissionsCount = submissionsDoc.exists() 
    ? (submissionsDoc.data().submissions || []).length 
    : 0;

  const analytics = funnelDoc.data()?.analytics || {
    visitors: 0,
    formClicks: 0,
    submissions: 0,
    dailyStats: {}
  };

  // Group submissions by date for daily stats
  const submissionsByDate: Record<string, number> = {};
  if (submissionsDoc.exists()) {
    submissionsDoc.data().submissions.forEach((submission: any) => {
      const date = new Date(submission.createdAt).toISOString().split('T')[0];
      submissionsByDate[date] = (submissionsByDate[date] || 0) + 1;
    });
  }

  // Merge daily stats with submission counts
  const dailyStats = Object.entries(analytics.dailyStats || {}).map(([date, stats]: [string, any]) => ({
    date,
    visitors: stats.visitors || 0,
    submissions: submissionsByDate[date] || 0
  })).sort((a, b) => a.date.localeCompare(b.date));

  return {
    visitors: analytics.visitors || 0,
    formClicks: analytics.formClicks || 0,
    submissions: submissionsCount,
    dailyStats
  };
};